/**
 * The Friends screen's state (docs/plans/009-friends.md): the player's profile and code, the friend list, one friend's book at a time, and a
 * code that arrived by link. Only fixed-list names and animal names are ever shown; nothing typed by another player exists here.
 */
import { AVATARS, DISPLAY_NAMES, formatFriendCode, isFriendCode, normalizeFriendCode, type FriendBookEntry } from "@zookeepbit/sim";
import type { BookEntryView, FriendsActions, FriendsView } from "../ui/view-model";
import { AccountError, type AccountApi, type Friend, type Profile } from "./api";

export interface FriendsDeps {
  api: AccountApi;
  isSignedIn(): boolean;
  onChange(): void;
  /** `window.location.origin`, for the share link. */
  origin: string;
}

const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

function entryView(e: FriendBookEntry): BookEntryView {
  return {
    id: e.weekId,
    name: capitalise(e.species),
    species: e.species,
    points: Math.floor(e.points),
    weekLabel: `Week ${Number(e.weekId.split("-W")[1])}`,
    looks: { ...e.looks },
  };
}

const codeOf = (error: unknown): string => (error instanceof AccountError ? error.code : "server_error");

export function createFriends(deps: FriendsDeps) {
  let loaded = false;
  let profile: Profile | null = null;
  let friends: Friend[] = [];
  let book: { friendId: string; entries: FriendBookEntry[] } | null = null;
  let problem: string | null = null;
  let busy = false;
  let pending: string | null = null;

  const changed = () => deps.onChange();

  /** Runs one request: busy while it runs, the problem code kept for the screen, never an exception to the caller. */
  async function run(work: () => Promise<void>): Promise<string | null> {
    if (!deps.isSignedIn()) return "not_signed_in";
    busy = true;
    problem = null;
    changed();
    try {
      await work();
      return null;
    } catch (error) {
      problem = codeOf(error);
      return problem;
    } finally {
      busy = false;
      changed();
    }
  }

  async function reloadFriends() {
    friends = await deps.api.listFriends();
  }

  const api = {
    view(): FriendsView {
      const signedIn = deps.isSignedIn();
      const shown = signedIn && profile ? profile : null;
      const owner = book ? friends.find((f) => f.id === book!.friendId) : undefined;
      return {
        signedIn,
        loaded: signedIn && loaded,
        names: [...DISPLAY_NAMES],
        avatars: AVATARS.map((a) => a.label),
        profile: shown
          ? {
              nameIndex: shown.nameIndex,
              avatarIndex: shown.avatarIndex,
              code: formatFriendCode(shown.friendCode),
              shareLink: `${deps.origin}/?friend=${shown.friendCode}`,
              bookShared: shown.bookShared,
            }
          : null,
        friends: signedIn
          ? friends.map((f) => ({
              id: f.id,
              name: DISPLAY_NAMES[f.nameIndex] ?? DISPLAY_NAMES[0]!,
              avatarIndex: f.avatarIndex,
              avatarLabel: AVATARS[f.avatarIndex]?.label ?? AVATARS[0]!.label,
              bookShared: f.bookShared,
              bookCount: f.bookCount,
            }))
          : [],
        book: signedIn && book ? { friendId: book.friendId, title: `${DISPLAY_NAMES[owner?.nameIndex ?? 0]}'s picture book`, entries: book.entries.map(entryView) } : null,
        problem,
        busy,
        pendingCode: pending === null ? null : formatFriendCode(pending),
      };
    },

    load: () =>
      run(async () => {
        profile = await deps.api.getProfile();
        await reloadFriends();
        loaded = true;
      }),

    setProfile: (nameIndex: number, avatarIndex: number) =>
      run(async () => {
        profile = await deps.api.putProfile({ nameIndex, avatarIndex, bookShared: profile?.bookShared ?? false });
      }),

    setBookShared: (bookShared: boolean) =>
      run(async () => {
        profile = await deps.api.putProfile({ nameIndex: profile?.nameIndex ?? 0, avatarIndex: profile?.avatarIndex ?? 0, bookShared });
      }),

    newCode: () =>
      run(async () => {
        const friendCode = await deps.api.rotateCode();
        if (profile) profile = { ...profile, friendCode };
      }),

    /** Resolves to null when added, otherwise the problem code. A wrong code never says which way it was wrong. */
    addFriend: (typed: string) =>
      run(async () => {
        const code = normalizeFriendCode(typed);
        await deps.api.addFriend(code);
        if (pending === code) pending = null;
        await reloadFriends();
      }),

    removeFriend: (id: string) =>
      run(async () => {
        await deps.api.removeFriend(id);
        if (book?.friendId === id) book = null;
        await reloadFriends();
      }),

    openBook: (id: string) =>
      run(async () => {
        book = { friendId: id, entries: await deps.api.getFriendBook(id) };
      }),

    closeBook() {
      book = null;
      changed();
    },

    /** A code from a link: remembered (in memory only) until added or dismissed. Ignored unless well formed. */
    setPendingCode(raw: string) {
      const code = normalizeFriendCode(raw);
      if (!isFriendCode(code)) return;
      pending = code;
      changed();
    },

    dismissPendingCode() {
      pending = null;
      changed();
    },

    /** Forget everything: sign-out, account deletion. */
    clear() {
      loaded = false;
      profile = null;
      friends = [];
      book = null;
      problem = null;
      busy = false;
      pending = null;
      changed();
    },
  };
  return api;
}

export type Friends = ReturnType<typeof createFriends>;

/** The actions the screen gets: the same calls without the return values the screen does not need. */
export function friendsActions(friends: Friends): FriendsActions {
  return {
    onLoadFriends: async () => void (await friends.load()),
    onSetProfile: async (n, a) => void (await friends.setProfile(n, a)),
    onSetBookShared: async (s) => void (await friends.setBookShared(s)),
    onNewCode: async () => void (await friends.newCode()),
    onAddFriend: (code) => friends.addFriend(code),
    onRemoveFriend: async (id) => void (await friends.removeFriend(id)),
    onOpenFriendBook: async (id) => void (await friends.openBook(id)),
    onCloseFriendBook: () => friends.closeBook(),
    onDismissPendingCode: () => friends.dismissPendingCode(),
  };
}

/**
 * Reads `?friend=CODE` from the address. Returns the code (null when it is not a well-formed code) and the address to put in the bar
 * instead, without the parameter (null when there was none to remove).
 */
export function parseFriendLink(search: string, pathname: string, hash: string): { code: string | null; cleaned: string | null } {
  const params = new URLSearchParams(search);
  if (!params.has("friend")) return { code: null, cleaned: null };
  const code = normalizeFriendCode(params.get("friend") ?? "");
  params.delete("friend");
  const rest = params.toString();
  return { code: isFriendCode(code) ? code : null, cleaned: `${pathname}${rest ? `?${rest}` : ""}${hash}` };
}
